import { BackTop, Table, Card, Row, Col, Statistic } from "antd";
import React, { useState, useEffect } from "react";
import LaboratoriesFilterBar from "../../../components/filter-bars/laboratories-report-filters";
import { cedisLocale, exportToExcel } from "../../../helpers/utilities";

export default function LaboratoriesReport({ data, isLoading }) {
  const [filteredData, setFilteredData] = useState([]);

  const handleReset = () => {
    setFilteredData(data);
  };

  const handleApplyFilter = (newData) => {
    setFilteredData(newData);
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Calculate metrics for the current filtered data
  const calculateMetrics = () => {
    const totalLabs = filteredData?.length;
    const completedLabs = filteredData?.filter((lab) => lab.isComplete)?.length;
    const pendingLabs = totalLabs - completedLabs;
    const totalCost = filteredData?.reduce(
      (sum, lab) => sum + (parseFloat(lab.bill) || 0),
      0
    );
    const completionRate =
      totalLabs > 0 ? (completedLabs / totalLabs) * 100 : 0;

    // Count unique lab types
    const uniqueLabTypes = [...new Set(filteredData?.map((lab) => lab.labType))]
      .length;

    // Count unique laboratories
    const uniqueLaboratories = [
      ...new Set(filteredData?.map((lab) => lab.laboratory?.laboratoryName)),
    ].filter(Boolean).length;

    return {
      totalLabs,
      completedLabs,
      pendingLabs,
      totalCost,
      completionRate,
      uniqueLabTypes,
      uniqueLaboratories,
    };
  };

  const columns = [
    {
      title: "#",
      sortDirections: ["descend", "ascend"],
      render: (t, r, i) => ++i,
    },
    {
      title: "Date - Time",
      sorter: (a, b) => a?.createdAt > b?.createdAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        new Date(record?.createdAt).toUTCString(),
    },
    {
      title: "Patient Name",
      sorter: (a, b) => a?.firstName > b?.lastName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record?.consultation?.patient?.firstName}  ${record?.consultation?.patient?.lastName}`,
    },
    // {
    //     title: "Insurance Provider",
    //     sorter: ( a, b ) => a?.consultation?.patient?.insuranceProvider?.providerName
    //         > b?.consultation?.patient?.insuranceProvider?.providerName,
    //     render: ( text, record, index ) =>
    //         record?.consultation?.patient?.insuranceProvider?.providerName

    // },
    {
      title: "Investigation",
      sorter: (a, b) =>
        a?.record?.consultation?.investigation >
        b?.record?.consultation?.investigation,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => record.consultation.investigation,
    },

    {
      title: "Laboratory Name",
      sorter: (a, b) =>
        a?.laboratory?.laboratoryName > b?.laboratory?.laboratoryName,
      dataIndex: ["laboratory", "laboratoryName"],
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Lab Type",
      sorter: (a, b) => a?.labType > b?.labType,
      dataIndex: "labType",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Lab Request",
      sorter: (a, b) => a?.requestType > b?.requestType,
      dataIndex: "requestType",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Status",
      sorter: (a, b) => a?.isComplete > b?.isComplete,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record.isComplete ? "Complete" : "Pending",
    },
    {
      title: "Bill",
      sorter: (a, b) => a?.bill > b?.bill,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => cedisLocale.format(record.bill),
    },
  ];

  const metrics = calculateMetrics();

  return (
    <div>
      <LaboratoriesFilterBar
        data={data}
        onReset={handleReset}
        onFilter={(d) => handleApplyFilter(d)}
        excelExport={() =>
          exportToExcel("laboratoriesReportTable", "rivia-laboratories-report")
        }
      />

      {/* Metrics Cards */}
      <Row gutter={16} className="mt-3 mb-3">
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Labs"
              value={metrics.totalLabs}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Completed"
              value={metrics.completedLabs}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Pending"
              value={metrics.pendingLabs}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Cost"
              value={cedisLocale.format(metrics.totalCost)}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Lab Types"
              value={metrics.uniqueLabTypes}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Completion Rate"
              value={metrics.completionRate.toFixed(1)}
              suffix="%"
              valueStyle={{
                color:
                  metrics.completionRate >= 80
                    ? "#3f8600"
                    : metrics.completionRate >= 60
                    ? "#faad14"
                    : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* advanced filters
            
            -> date range
            -> cost
            -> status
            -> gender
            
            */}
      <Table
        className="border mt-3"
        id="laboratoriesReportTable"
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        bordered
      />
      <BackTop />
    </div>
  );
}
